// src/components/store/sections/featured-products/Motion3DProductCard.tsx
"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Star, ShoppingBag, Heart, Check, Sparkles, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";

export interface ProductItem {
  id: number | string;
  name: string;
  slug?: string;
  price: number | string;
  regular_price?: number | string;
  compare_price?: number | string;
  image?: string | null;
  primary_image?: string | null;
  category_name?: string;
  description?: string;
  rating?: number;
}

// 🌟 ভিডিওর ৩টি নিয়ন কালার ভ্যারিয়েন্ট (Pink/Rose, Mint/Cyan, Cyber Violet)
const COLOR_VARIANTS = [
  {
    id: "rose",
    name: "Neon Rose",
    hex: "#ec4899",
    glow: "rgba(236, 72, 153, 0.45)",
    boxGradient: "from-pink-950/60 via-purple-950/40 to-slate-950",
    btnGradient: "from-pink-500 via-rose-500 to-purple-600",
  },
  {
    id: "cyan",
    name: "Cyan Pulse",
    hex: "#06b6d4",
    glow: "rgba(6, 182, 212, 0.45)",
    boxGradient: "from-cyan-950/60 via-slate-950 to-slate-950",
    btnGradient: "from-cyan-500 via-teal-500 to-blue-600",
  },
  {
    id: "violet",
    name: "Cyber Violet",
    hex: "#8b5cf6",
    glow: "rgba(139, 92, 246, 0.45)",
    boxGradient: "from-purple-950/60 via-indigo-950 to-slate-950",
    btnGradient: "from-purple-600 via-indigo-600 to-pink-600",
  },
];

interface Motion3DProductCardProps {
  product: ProductItem;
  index?: number;
}

export default function Motion3DProductCard({
  product,
  index = 0,
}: Motion3DProductCardProps) {
  const [selectedColor, setSelectedColor] = useState(COLOR_VARIANTS[index % COLOR_VARIANTS.length]);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [adding, setAdding] = useState(false);
  const { addToCart, openCart } = useCart();

  // 🌟 Framer Motion 3D Tilt Physics
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x, { stiffness: 150, damping: 20 });
  const mouseYSpring = useSpring(y, { stiffness: 150, damping: 20 });

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["14deg", "-14deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-16deg", "16deg"]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    x.set(mouseX / width - 0.5);
    y.set(mouseY / height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const formatImageUrl = (img?: string | null) => {
    if (!img) return "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&auto=format&fit=crop&q=80";
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  const productImg = formatImageUrl(product.primary_image || product.image);
  const priceNum = Number(product.price) || 89;
  const comparePrice = Number(product.compare_price || product.regular_price) || Math.round(priceNum * 1.35);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(true);
    try {
      addToCart(product, 1);
      if (openCart) openCart();
    } catch (err) {
      console.error(err);
    }
    setTimeout(() => setAdding(false), 600);
  };

  return (
    <div className="relative w-full py-4 px-1 select-none">
      {/* 🌟 ভিডিওর বাম ও ডান পাশের ফ্লোটিং ব্যাজ (ডেস্কটপে দৃশ্যমান) */}
      <div className="hidden lg:flex items-center justify-between absolute -top-1 left-2 right-2 z-20 pointer-events-none">
        <span className="px-2.5 py-1 rounded-full bg-slate-900/90 border border-pink-500/30 text-[9px] font-black text-pink-400 uppercase tracking-wider backdrop-blur-md shadow-lg shadow-pink-950/40 pointer-events-auto">
          -25% Launch special
        </span>
        <span className="px-2.5 py-1 rounded-full bg-slate-900/90 border border-emerald-500/30 text-[9px] font-black text-emerald-400 uppercase tracking-wider backdrop-blur-md shadow-lg shadow-emerald-950/40 pointer-events-auto flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          In stock
        </span>
      </div>

      {/* 🌟 3D পার্সপেক্টিভ কন্টেইনার */}
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
        }}
        className="relative w-full rounded-[28px] sm:rounded-[32px] p-3 sm:p-5 bg-[#0f0e17]/95 border border-purple-500/20 shadow-2xl hover:border-purple-500/50 transition-colors duration-300 flex flex-col justify-between group overflow-hidden"
      >
        {/* ব্যাকগ্রাউন্ড ডাইনামিক অ্যাম্বিয়েন্ট গ্লো */}
        <div
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full blur-[70px] pointer-events-none transition-colors duration-700"
          style={{ backgroundColor: selectedColor.glow }}
        />

        {/* 🌟 ১. ওপরের ৩D প্রোডাক্ট ফ্রেম (১০০% ফুল-ব্লিড ফিট) */}
        <div
          className="relative w-full h-48 sm:h-60 rounded-2xl sm:rounded-3xl border border-white/10 overflow-hidden bg-slate-950 transition-all duration-700"
          style={{ transform: "translateZ(30px)" }}
        >
          {/* ফ্রেম সম্পূর্ণ জুড়ে থাকা প্রোডাক্ট ও ৩D স্টেজ ইমেজ */}
          <Link
            href={`/products/${product.slug || product.id}`}
            className="absolute inset-0 w-full h-full block group/img"
          >
            <Image
              src={productImg}
              alt={product.name}
              fill
              unoptimized
              className="object-cover group-hover:scale-105 transition-transform duration-500"
            />
            {/* ওপরের টেক্সট ও ব্যাজ সুন্দরভাবে ফুটানোর জন্য সফট গ্রেডিয়েন্ট ওভারলে */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/35 pointer-events-none" />
          </Link>

          {/* ফ্রেমের টপ ব্যাজ ও উইশলিস্ট বাটন */}
          <div className="relative flex items-center justify-between p-3 z-10 pointer-events-none">
            <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-black/50 backdrop-blur-md border border-white/15 text-[9px] sm:text-[10px] font-black text-white/90 uppercase tracking-widest flex items-center gap-1 pointer-events-auto">
              <Sparkles className="w-2.5 h-2.5 text-amber-400" />
              BEST SELLER
            </span>

            <button
              type="button"
              onClick={() => setIsWishlisted(!isWishlisted)}
              className="p-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15 text-white/70 hover:text-rose-400 hover:scale-110 active:scale-90 transition-all cursor-pointer pointer-events-auto"
            >
              <Heart
                className={`w-3.5 h-3.5 ${
                  isWishlisted ? "fill-rose-500 text-rose-500" : ""
                }`}
              />
            </button>
          </div>

          {/* ইন্টারেক্টিভ টিল্ট ইন্ডিকেটর টেক্সট */}
          <div className="absolute bottom-2 inset-x-0 text-center z-10 pointer-events-none">
            <span className="text-[9px] font-mono text-white/70 tracking-wider uppercase drop-shadow-md">
              Hover to 3D Inspect
            </span>
          </div>
        </div>

        {/* 🌟 ২. প্রোডাক্ট ইনফরমেশন এরিয়া */}
        <div
          className="pt-3.5 sm:pt-4 space-y-2 sm:space-y-2.5 text-left"
          style={{ transform: "translateZ(25px)" }}
        >
          {/* ক্যাটাগরি ট্যাগ */}
          <span className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-slate-400 font-bold block">
            {product.category_name || "PERFORMANCE FOOTWEAR"}
          </span>

          {/* টাইটেল */}
          <Link href={`/products/${product.slug || product.id}`} className="block">
            <h3 className="text-sm sm:text-base font-black text-white tracking-tight truncate hover:text-purple-300 transition-colors">
              {product.name || "Aero Pulse X1"}
            </h3>
          </Link>

          {/* স্টার রেটিং */}
          <div className="flex items-center gap-1.5">
            <div className="flex items-center text-amber-400">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-3 h-3 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="text-[10px] text-slate-400 font-bold">
              ({product.rating || "4.9"})
            </span>
          </div>

          {/* প্রাইস এবং কালার সোয়াচ পিকার (Color Dots) */}
          <div className="flex items-center justify-between pt-1">
            <div className="flex items-baseline gap-1.5">
              <span className="text-base sm:text-xl font-black text-white font-mono">
                ${Math.round(priceNum)}.00
              </span>
              <span className="text-[11px] sm:text-xs text-slate-500 line-through font-mono">
                ${Math.round(comparePrice)}.00
              </span>
            </div>

            {/* ভিডিওর মতো ৩টি কালার ডট */}
            <div className="flex items-center gap-1.5 p-1 rounded-full bg-slate-900/80 border border-white/10">
              {COLOR_VARIANTS.map((c) => {
                const isActive = selectedColor.id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedColor(c)}
                    style={{ backgroundColor: c.hex }}
                    className={`w-3.5 h-3.5 rounded-full transition-transform cursor-pointer relative flex items-center justify-center ${
                      isActive ? "scale-125 ring-2 ring-white/60" : "opacity-70 hover:opacity-100"
                    }`}
                    title={c.name}
                  >
                    {isActive && <Check className="w-2 h-2 text-white stroke-[4]" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 🌟 ৩. নিয়ন গ্রেডিয়েন্ট "ADD TO CART" বাটন */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={adding}
              style={{
                boxShadow: `0 8px 24px ${selectedColor.glow}`,
              }}
              className={`w-full py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-gradient-to-r ${selectedColor.btnGradient} text-white font-black text-[10px] sm:text-xs uppercase tracking-wider transition-all duration-300 hover:brightness-110 active:scale-95 cursor-pointer flex items-center justify-center gap-2`}
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">
                {adding ? "Adding..." : "Add to cart"}
              </span>
              <span className="sm:hidden font-bold">
                {adding ? "..." : "+ Add"}
              </span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}